import { Sun, Moon, Home } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PageView, Module } from '@/types'
import type { useProgress } from '@/hooks/useProgress'

interface TopBarProps {
  currentView: PageView
  modules: Module[]
  progress: ReturnType<typeof useProgress>
  darkMode: boolean
  onToggleDark: () => void
  onNavigate: (view: PageView) => void
}

export function TopBar({
  currentView,
  modules,
  progress,
  darkMode,
  onToggleDark,
  onNavigate,
}: TopBarProps) {
  const activeModule =
    currentView.page === 'module'
      ? modules.find(m => m.id === currentView.moduleId)
      : null

  const title =
    currentView.page === 'dashboard'
      ? 'Partner Selling Methods'
      : currentView.page === 'quick-reference'
        ? 'Quick Reference'
        : activeModule
          ? `Method ${activeModule.number}: ${activeModule.title}`
          : ''

  return (
    <header className="flex items-center gap-4 px-6 h-14 bg-[var(--bg-primary)] border-b border-[var(--border)] flex-shrink-0 no-print">
      {/* Breadcrumb / title */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {currentView.page !== 'dashboard' && (
          <button
            onClick={() => onNavigate({ page: 'dashboard' })}
            className="flex-shrink-0 p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
            aria-label="Back to dashboard"
          >
            <Home size={16} />
          </button>
        )}
        <h1 className={cn('font-semibold text-sm text-[var(--text-primary)] truncate')}>
          {title}
        </h1>
      </div>

      {/* Progress pill */}
      <div className="hidden sm:flex items-center gap-2">
        <span className="text-xs text-[var(--text-muted)]">
          {progress.completedCount}/{progress.totalCount} complete
        </span>
        <div className="w-24 h-1.5 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--accent-blue)] rounded-full transition-all duration-500"
            style={{ width: `${progress.progressPercent}%` }}
          />
        </div>
      </div>

      {/* Dark mode toggle */}
      <button
        onClick={onToggleDark}
        className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {darkMode ? <Sun size={16} /> : <Moon size={16} />}
      </button>
    </header>
  )
}
